/**
 * Wishlist page (/apps/wishlist): flag golf cards so wishlist-page.css can hide their add to cart.
 *
 * The page and its `<wishlist-product-card>` elements are rendered by Swish (Wishlist King), so the
 * theme can't output the card markup and the DOM carries no product tags. The Swish window API
 * does (`item.product.tags`), so the wishlist is read with `state.observeWishlist` (same API as
 * wishlist-card-button.js) and `data-golf` is set on the cards of items tagged `golf`.
 * Fail open: if the SDK doesn't load, nothing is flagged and the cards keep their add to cart.
 */
(() => {
  if (!location.pathname.includes('/apps/wishlist')) return;
  // Guards against this script running twice on the same page (a second run would double the
  // subscriptions and observers).
  if (window.__wishlistPageInit) return;
  window.__wishlistPageInit = true;

  const CARD_SELECTOR = 'wishlist-product-card[data-wishlist-item-id]';
  const GOLF_TAG = 'golf';

  /** Ids of the wishlist items tagged golf, by wishlist id (`mine` or a shared wishlist's id). */
  const golfItemIds = new Map();
  /** Wishlist ids already subscribed to. */
  const subscribed = new Set();

  /** @type {Promise<any> | undefined} */
  let appPromise;

  /** Resolves with `window.WishlistKing` as soon as the Swish SDK has loaded. */
  function getApp() {
    appPromise ??= new Promise((resolve) => {
      if (window.WishlistKing) {
        resolve(window.WishlistKing);
      } else {
        document.addEventListener('wk:app:ready', () => resolve(window.WishlistKing), { once: true });
      }
    });
    return appPromise;
  }

  function flagGolfCards() {
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const ids = golfItemIds.get(card.getAttribute('data-wishlist-id') || 'mine');
      card.toggleAttribute('data-golf', Boolean(ids?.has(card.getAttribute('data-wishlist-item-id'))));
    });
  }

  /** @param {string} wishlistId */
  async function subscribe(wishlistId) {
    subscribed.add(wishlistId);
    try {
      const app = await getApp();
      app.state.observeWishlist({ wishlistId }).subscribe((wishlist) => {
        golfItemIds.set(
          wishlistId,
          new Set(
            wishlist.items
              .filter((item) => (item.product.tags || []).some((tag) => String(tag).trim().toLowerCase() === GOLF_TAG))
              .map((item) => item.id)
          )
        );
        flagGolfCards();
      });
    } catch (error) {
      console.warn('[Wishlist page] could not read the Swish wishlist state:', error);
    }
  }

  let timer;
  function scan() {
    timer = undefined;
    document.querySelectorAll(CARD_SELECTOR).forEach((card) => {
      const wishlistId = card.getAttribute('data-wishlist-id') || 'mine';
      if (!subscribed.has(wishlistId)) subscribe(wishlistId);
    });
    flagGolfCards();
  }
  function queueScan() {
    if (timer) return;
    timer = setTimeout(scan, 50);
  }

  // The cards render asynchronously and Swish can recreate them, so flag them whenever the DOM changes.
  new MutationObserver(queueScan).observe(document.body, { childList: true, subtree: true });
  queueScan();
})();
